import React, { useState } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  useColorModeValue,
  Box,
  Flex,
} from "@chakra-ui/react";
import { CustomText } from "@root/components/common/components";
import { useTranslation } from "react-i18next";
import { getTableColumns } from "./tableColumns";
import { TABLE_STYLES } from "./actionConstants";
import { renderActionButton } from "@root/utils/assessment-action-button";
import { ASSESSMENT_STATUS } from "./actionConstants";
import { Paginate } from "@root/components/pagination";
import { PAGINATION_DEFAULTS } from "@root/constants";
import TrashIcon from "@root/assets/icons/trashicon";
import { PauseButtonTable } from "@root/components/common/PauseButtonTable";
import { RiskAssessmentService } from "@root/services/risk-assessment";
import { useRiskAssessmentContext } from "@root/context/risk-assessment-context";
import { ASSESSMENT_TYPE_PARAM } from "@root/constants/common";
import { DeleteAssessmentModal } from "@root/components/modals";

interface Assessment {
  id: string;
  department: string;
  assessmentName: string;
  equipment: string;
  material: string;
  workDescription: string;
  date: string;
  status:
    | "pending"
    | "completed"
    | "in_progress"
    | "processing_sources"
    | "failed"
    | "irrelevant_doc";
}

interface RecentAssessmentsTableProps {
  assessments: Assessment[];
  onStartAnalysis: (assessment: Assessment) => void;
  onViewOutput: (assessment: Assessment) => void;
  onRetry: (assessment: Assessment) => void;
  pagination?: boolean;
  count?: number;
  pageSize?: number;
  page?: number;
  onPageChange?: (page: number) => void;
  highlightedAssessmentId?: string;
}

const RecentAssessmentsTable: React.FC<RecentAssessmentsTableProps> = ({
  assessments,
  onStartAnalysis,
  onViewOutput,
  onRetry,
  pagination = false,
  count = 0,
  pageSize = PAGINATION_DEFAULTS.PAGESIZE,
  page = 1,
  onPageChange,
  highlightedAssessmentId,
}) => {
  const { t } = useTranslation("common");
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const tableColumns = getTableColumns(t);
  const service = new RiskAssessmentService();
  const { fetchRiskAssessments } = useRiskAssessmentContext();
  const [deleting, setDeleting] = useState(false);
  const [selected, setSelected] = useState<Assessment | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Helper function to render table cell content based on column key
  const renderCellContent = (assessment: Assessment, columnKey: string) => {
    switch (columnKey) {
      case "department":
        return <CustomText fontSize="14px" text={assessment?.department} />;
      case "assessmentName":
        return <CustomText fontSize="14px" text={assessment?.assessmentName} />;
      case "equipment":
        return <CustomText fontSize="14px" text={assessment?.equipment} />;
      case "material":
        return <CustomText fontSize="14px" text={assessment?.material} />;
      case "workDescription":
        return (
          <CustomText
            fontSize="14px"
            text={assessment?.workDescription}
            noOfLines={2}
          />
        );
      case "date":
        return <CustomText fontSize="14px" text={assessment?.date} />;
      case "action":
        return renderActionButton({
          assessment,
          onStartAnalysis,
          onViewOutput,
          onRetry,
          t,
        });
      case "delete":
        return assessment?.status !== ASSESSMENT_STATUS.IN_PROGRESS &&
          assessment?.status !== ASSESSMENT_STATUS.PROCESSING_SOURCES ? (
          <Box
            as="button"
            onClick={() => {
              setSelected(assessment);
              setIsModalOpen(true);
            }}
          >
            <TrashIcon />
          </Box>
        ) : (
          <PauseButtonTable onClick={() => {}} showSpinner={false} />
        );
      default:
        return null;
    }
  };

  return (
    <Box>
      <TableContainer
        bg={bgColor}
        borderRadius={TABLE_STYLES?.borderRadius}
        border="1px solid"
        borderColor={borderColor}
        overflowX="auto"
      >
        <Table variant="simple" size="md">
          <Thead bg={TABLE_STYLES?.headerBg}>
            <Tr>
              {tableColumns?.map((column) => (
                <Th
                  key={column?.key}
                  fontSize={TABLE_STYLES?.headerFontSize}
                  fontWeight={TABLE_STYLES?.headerFontWeight}
                  color={TABLE_STYLES?.headerTextColor}
                  py={TABLE_STYLES?.cellPadding}
                  width={column?.width}
                >
                  {column?.label}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {assessments?.map((assessment) => {
              const isHighlighted =
                highlightedAssessmentId &&
                String(assessment?.id) === String(highlightedAssessmentId);
              return (
                <Tr
                  key={assessment?.id}
                  bg={isHighlighted ? "rgba(0, 94, 184, 0.08)" : undefined}
                  _hover={{ bg: TABLE_STYLES?.hoverBg }}
                >
                  {tableColumns?.map((column) => (
                    <Td
                      key={column?.key}
                      py={TABLE_STYLES?.cellPadding}
                      maxW={column?.width === "300px" ? "300px" : undefined}
                    >
                      {renderCellContent(assessment, column?.key)}
                    </Td>
                  ))}
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {pagination && onPageChange && (
        <Flex justify="center" mt={6}>
          <Paginate
            count={count}
            pageSize={pageSize}
            page={page}
            onPageChange={onPageChange}
          />
        </Flex>
      )}

      <DeleteAssessmentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        isLoading={deleting}
        assessmentName={selected?.assessmentName}
        onConfirm={async () => {
          if (!selected) return;
          try {
            setDeleting(true);
            await service?.deleteRiskAssessment(Number(selected?.id));
            setIsModalOpen(false);
            setSelected(null);
            await fetchRiskAssessments({
              limit: PAGINATION_DEFAULTS.PAGESIZE,
              offset: (page - 1) * pageSize,
              assessmentType: ASSESSMENT_TYPE_PARAM.CREATED,
            });
          } finally {
            setDeleting(false);
          }
        }}
      />
    </Box>
  );
};

export default RecentAssessmentsTable;
